import { Component, computed, effect, input, InputSignal, output, OutputEmitterRef, signal, Signal, inject, Type, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MDContentBoxComponent, MDDialogService, MDTagComponent } from '@melodic-ui/components';
import { UserIconComponent } from '../../user-icon/user-icon.component';
import { interval, take } from 'rxjs';
import { IMDMenuOption, MDMenuComponent } from '@melodic-ui/components/menu/menu.component';
import { MDIconComponent } from '@melodic-ui/components/icon/icon.component';
import { AuthenticationService } from '../../../auth/services/authentication.service';
import { LessonService } from '@lb/api/mylb/services/lesson.service';
import { ConfirmationDialogComponent } from '@lb/components/confirmation-dialog/confirmation-dialog.component';
import { RescheduleLessonDialogService } from '@lb/services/reschedule-lesson-dialog.service';
import { SubstituteInstructorDialogService } from '@lb/components/substitute-instructor-dialog/substitute-instructor-dialog.service';
import { LessonTimezoneService } from '../../../services/lesson-timezone.service';
import { TzDatePipe, TzTimePipe } from '../../../pipes/timezone-aware-date.pipe';
import { GlobalTimezoneService } from '../../../services/global-timezone.service';
import { buildLessonTags } from '../utils/lesson-tags.util';
import { TimezoneStateService } from '@lb/state/timezone-state.service';
import { AccountStateService } from '@lb/state/account-state.service';
import { UserStateService } from '@lb/state/user-state.service';
import { RouterLink } from '@angular/router';
import { AttendanceService } from '@lb/api/mylb/services';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { IUserSummary } from '@lb/api/mylb/interfaces/iuser-summary.interface';
import { canRescheduleLesson, isLessonEditable } from '../utils/lesson-helper.util';
import { getLessonTypeVariant } from '../utils/lesson-type-colors.util';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { usesLessonDetailsV2 } from '@lb/functions';

interface ILessonUserInformation {
	userID?: number;
	firstName?: string;
	lastName?: string;
	preferredName?: string;
	fullName?: string;
}

@Component({
	selector: 'lb-lesson-card',
	standalone: true,
	imports: [CommonModule, MDContentBoxComponent, MDTagComponent, UserIconComponent, MDMenuComponent, MDIconComponent, TzDatePipe, TzTimePipe, RouterLink],
	templateUrl: './lesson-card.component.html',
	styleUrl: './lesson-card.component.scss'
})
export class LessonCardComponent {
	private _currentTime = signal(new Date());
	private _authService = inject(AuthenticationService);
	private _lessonService = inject(LessonService);
	private _attendanceService = inject(AttendanceService);
	private _dialogService = inject(MDDialogService);
	private _rescheduleLessonDialogService = inject(RescheduleLessonDialogService);
	private _accountStateService = inject(AccountStateService);
	private _timezoneStateService = inject(TimezoneStateService);
	private _substituteInstructorDialogService = inject(SubstituteInstructorDialogService);
	private _lessonTimezoneService = inject(LessonTimezoneService);
	private _globalTimezoneService = inject(GlobalTimezoneService);
	private _userStateService = inject(UserStateService);
	private readonly appSettings = inject<IAppSettings>(APP_CONFIG);

	public lesson: InputSignal<ILessonListItem> = input.required<ILessonListItem>();
	public lessonView: InputSignal<'learner' | 'instructor' | 'history'> = input<'learner' | 'instructor' | 'history'>('learner');
	public displayedUser: InputSignal<'learner' | 'instructor'> = input<'learner' | 'instructor'>('instructor');
	public isPopup: InputSignal<boolean> = input.required();
	public adminUrl: InputSignal<string> = input<string>('');
	public detailsLink = input<(string | number)[] | null>(null);
	public linkView = computed(() => (this.displayedUser() === 'instructor' ? 'learner' : 'instructor'));

	public isUserAdmin = this._accountStateService.isAdmin;
	public pendingReschedule = computed(() => this.lesson().lessonStatusID === 3);
	public isLearnerAbsent = computed(() => this.lesson().attendanceStatusID === 2);

	// Get available timezones from global state
	private _availableTimezones = this._timezoneStateService.timezones;

	// Watch for timezone changes to force re-rendering
	private _effectiveTimezone = this._globalTimezoneService.effectiveTimezone;

	// Computed lesson start/end times that update when timezone changes
	public lessonDate = computed(() => {
		const lesson = this.lesson();
		const timezone = this._effectiveTimezone(); // This dependency will trigger updates
		const startDate = lesson.lessonDate;

		// Force pipe re-evaluation by creating a new Date object when timezone changes
		return timezone ? new Date(startDate) : new Date(startDate);
	});

	public lessonEndTime = computed(() => {
		const lesson = this.lesson();
		const timezone = this._effectiveTimezone(); // This dependency will trigger updates
		const endDate = lesson.lessonEndDate;

		// Force pipe re-evaluation by creating a new Date object when timezone changes
		return timezone ? new Date(endDate) : new Date(endDate);
	});

	// User summaries pulled from UserStateService
	public learnerSummary = computed<IUserSummary | null>(() => {
		const userID = this.lesson().learnerUserID;
		return this._userStateService.getUserSummary(userID)();
	});

	public instructorSummary = computed<IUserSummary | null>(() => {
		const userID = this.lesson().instructorUserID;
		return userID ? this._userStateService.getUserSummary(userID)() : null;
	});

	// Timezone-aware lesson time conversion
	public lessonTimeConversion = computed(() => {
		const lesson = this.lesson();
		const timezones = this._availableTimezones();
		const instructorTimezoneID = this.instructorSummary()?.timezoneID;

		const conversion = this._lessonTimezoneService.convertLessonTime(lesson.lessonDate, instructorTimezoneID, timezones);

		return conversion;
	});

	// Show timezone context when times differ or user explicitly enabled it
	public showTimezoneContext = computed(() => {
		const conversion = this.lessonTimeConversion();
		return conversion.timezonesDiffer || this._lessonTimezoneService.timezoneContext().showTimezoneSelector;
	});

	// Get timezone abbreviation for display
	public timezoneAbbreviation = computed(() => {
		const conversion = this.lessonTimeConversion();
		return conversion.displayTimezone?.abbreviation || '';
	});

	public lessonUser: Signal<ILessonUserInformation> = computed(() => {
		const lesson = this.lesson();
		const summary = this.displayedUser() === 'learner' ? this.learnerSummary() : this.instructorSummary();
		const userID = this.displayedUser() === 'learner' ? lesson.learnerUserID : lesson.instructorUserID;

		return {
			userID,
			firstName: summary?.firstName,
			lastName: summary?.lastName,
			preferredName: summary?.preferredName,
			fullName: summary ? `${summary.firstName} ${summary.lastName}`.trim() : undefined
		};
	});

	public dataChanged: OutputEmitterRef<void> = output();

	currentTime = signal(new Date());
	public canReschedule = computed(() => {
		return canRescheduleLesson(this.lessonView(), this.lesson(), this.currentTime());
	});

	/** False on a lesson from another instructor's era (predecessor or substitute): the viewer can read it, not log it. */
	public isEditable = computed(() => isLessonEditable(this.lesson()));

	// Chirp room lessons run on the room's shared schedule, so learners never reschedule them.
	public isChirpLesson = computed(() => getLessonTypeVariant(this.lesson().lessonTypeID) === 'chirp');

	private _lessonTagsSummary = computed(() =>
		buildLessonTags({
			lesson: this.lesson(),
			view: this.lessonView(),
			referenceTime: this.currentTime()
		})
	);

	public tags = computed(() => this._lessonTagsSummary().tags);
	public hasError: Signal<boolean> = computed(() => this._lessonTagsSummary().hasError);
	public hasWarning: Signal<boolean> = computed(() => this._lessonTagsSummary().hasWarning);
	// The "Details" link routes into the v2 lesson-details experience, so it must follow per-lesson eligibility.
	public lessonDetailsEnabled = computed(() => usesLessonDetailsV2(this.lesson(), this.appSettings));

	public menuOptions = computed<IMDMenuOption[]>(() => {
		const options: IMDMenuOption[] = [];

		// Reschedule option (existing logic)
		if (this.canReschedule()) {
			options.push({ label: 'Reschedule', icon: 'untitled-ui/time/clock-refresh', click: () => this.reschedule() });
		}

		// Admin options
		if (this._authService.hasEntitlement('Admin:App:Access')) {
			// sub
			if (
				this.lesson().lessonStatusID !== 5 &&
				(this.lesson().lessonTypeID === 4 || this._authService.hasEntitlement('Admin:Lesson:InstructorOverride'))
			) {
				options.push({ label: 'Change Instructor', icon: '', click: () => this.openSubstituteInstructorDialog() });
			}

			// Admin can always reschedule if regular reschedule is not available
			if (!this.canReschedule()) {
				options.push({ label: 'Reschedule', icon: 'untitled-ui/time/clock-refresh', click: () => this.reschedule() });
			}

			// Delete Lesson: always for admins
			options.push({ label: 'Delete Lesson', icon: '', click: () => this.deleteLesson() });

			// Reset Attendance: only if attendance is set
			if (this.isAttendanceSet()) {
				options.push({ label: 'Reset Attendance', icon: '', click: () => this.resetAttendance() });
			}
		}

		return options;
	});

	// Returns true if attendance has been set for the lesson
	public isAttendanceSet(): boolean {
		const lesson = this.lesson();
		return !!lesson?.attendanceStatusID;
	}

	public reschedule() {
		this._rescheduleLessonDialogService
			.open(this.lesson())
			.pipe(take(1))
			.subscribe((newLessonHistory) => {
				if (newLessonHistory) {
					this.dataChanged.emit();
				}
			});
	}

	public resetAttendance() {
		const dialogRef = this._dialogService.open(ConfirmationDialogComponent as Type<Component>, {
			title: 'Reset Attendance',
			body: 'Are you sure you want to reset attendance for this lesson?',
			icon: 'untitled-ui/general/check-done-02',
			successText: 'Reset',
			action: () => this._attendanceService.resetAttendance(this.lesson().lessonID)
		});

		dialogRef.afterClosed.subscribe({
			next: (confirmed) => {
				if (confirmed) {
					this.dataChanged.emit();
				}
			}
		});
	}

	public openSubstituteInstructorDialog() {
		// Use the service method to handle enrollment lookup and subscribe to result
		this._substituteInstructorDialogService
			.openByEnrollmentId(this.lesson().enrollmentID, [this.lesson().lessonID], {
				userID: this.lesson().instructorUserID,
				fullName: this.lesson().instructorFullName
			})
			.pipe(take(1))
			.subscribe({
				next: (result) => {
					// If result is truthy (instructor was selected and updated), emit data change
					if (result) {
						this.dataChanged.emit();
					}
				},
				error: (error) => {
					console.error('Error in substitute instructor dialog:', error);
				}
			});
	}

	public deleteLesson() {
		const dialogRef = this._dialogService.open(ConfirmationDialogComponent as Type<Component>, {
			title: 'Delete Lesson',
			body: 'Are you sure you want to delete this lesson? This action cannot be undone.',
			icon: 'untitled-ui/general/check-done-02',
			successText: 'Delete',
			action: () => this._lessonService.deleteLesson(this.lesson().lessonID)
		});

		dialogRef.afterClosed.subscribe({
			next: (confirmed) => {
				if (confirmed) {
					this.dataChanged.emit();
				}
			}
		});
	}

	openLink(type: 'instructor' | 'enrollment' | 'learner'): void {
		if (!this.isUserAdmin() || this.adminUrl() === '') {
			return;
		}

		console.log(type);
		switch (type) {
			case 'instructor':
				window.open(`${this.adminUrl()}/instructor/${this.lesson().instructorUserID}/basic-information`, '_blank');
				break;
			case 'learner':
				window.open(`${this.adminUrl()}/learner/${this.lesson().learnerUserID}/basic-information`, '_blank');
				break;
			default:
				console.error('Invalid type provided for openLink:', type);
				break;
		}
	}

	constructor() {
		interval(10000).subscribe(() => {
			this._currentTime.set(new Date());
		});

		// Load user summaries when lesson input changes.
		// untracked() prevents the signal reads inside loadUserSummaries (e.g. userSummaries())
		// from being registered as dependencies of this effect, which would cause it to
		// re-trigger on every store update and create an O(N²) cascade with lesson lists.
		// This should not result in a request often since previous summaries are loaded into the cache
		// making this a fail safe for cache misses without causing excessive requests on store updates.
		effect(() => {
			const lesson = this.lesson();
			const userIDs = [lesson.learnerUserID, lesson.instructorUserID].filter((id): id is number => id != null);
			untracked(() => this._userStateService.loadUserSummaries(userIDs));
		});
	}
}
